import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    NgZone,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {noop} from '@angular/core/src/linker/view_utils';
import {MapsAPILoader} from 'angular2-google-maps/core';
import {AddressMapper} from './address-mapper.service';
import isEqual = require('lodash/isEqual');

// region NG_VALUE_ACCESSOR
const MY_ADDRESSPICKER_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AddresspickerComponent),
    multi: true
};
// endregion

// region Other Classes
export class AddressModel {
    public address1: string = '';
    public postalCode: string = '';
    public city: string = '';
    public stateOrProvince: string = '';
    public country: string = '';
}
// endregion

let nextUniqueId = 0;

/**
 * Created by Arturo Campos on 23/01/17.
 */
@Component({
    selector: 'my-addresspicker',
    template: require('./addresspicker.component.html'),
    styles: [require('./addresspicker.component.scss')],
    providers: [MY_ADDRESSPICKER_CONTROL_VALUE_ACCESSOR]
})
export class AddresspickerComponent implements ControlValueAccessor, OnInit {

    readonly id = `my-addresspicker-${++nextUniqueId}`;

    // region Input
    @Input() placeholder: string;
    @Input() name: string | null = null;
    @Input() errorMessage: String;
    @Input() errorMessagePosition: String = 'right';
    // endregion

    // region Output
    @Output() addressUpdated = new EventEmitter<AddressModel>();
    // endregion

    // region Children
    @ViewChild('input') private _inputElement: ElementRef;
    // endregion

    private _value?: string;

    get value(): string | undefined {
        return this._value;
    };

    set value(value: string | undefined) {
        let newValue: string | undefined = value;
        if (!isEqual(this._value, newValue)) {
            this._value = newValue;
            this._onChangeCallback();
        }
    }

    private focused: boolean = false;

    get empty() {
        return !this._value;
    }

    constructor(private readonly _el: ElementRef,
                private readonly _mapsAPILoader: MapsAPILoader,
                private readonly _addressMapper: AddressMapper,
                private readonly _zone: NgZone) {
    }

    // region Events
    onFocus() {
        this.focused = true;
    }

    onBlur() {
        this.focused = false;
        this._onTouchedCallback();
    }

    handleChange = () => {
        this.value = this._inputElement.nativeElement.value;
        this._onTouchedCallback();
    }

    @HostListener('keydown.tab', ['$event'])
    onTab(event: KeyboardEvent) {
        event.preventDefault();
        this.nextInputElement().focus();
    }

    private nextInputElement() {
        return $(this._el.nativeElement)
            .nextAll()
            .map((i, s) => $(s).find('input').first()[0])
            .first();
    }

    ngOnInit() {
        this._mapsAPILoader.load().then(() => {
            let autocomplete = new google.maps.places.Autocomplete(this._inputElement.nativeElement, {
                types: ['geocode']
            });
            autocomplete.addListener('place_changed', () => {
                let place: google.maps.places.PlaceResult = autocomplete.getPlace();
                if (place) {
                    this._zone.run(() => {
                        let addressModel: AddressModel = this._addressMapper.map(place);
                        this._inputElement.nativeElement.value = addressModel.address1;
                        this.addressUpdated.emit(addressModel);
                    });
                }
            });
        });
    }

    // endregion

    // region ControlValueAccessor
    /** Callback registered via registerOnTouched (ControlValueAccessor) */
    private _onTouchedCallback: () => void = noop;
    /** Callback registered via registerOnChange (ControlValueAccessor) */
    private _onChangeCallback: () => void = noop;

    writeValue(value: string | null | undefined): void {
        this._value = value ? value : undefined;
    }

    registerOnChange(fn: any): void {
        this._onChangeCallback = () => fn(this._value ? this._value : undefined);
    }

    registerOnTouched(fn: any): void {
        this._onTouchedCallback = fn;
    }

    // endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/controls/addresspicker/addresspicker.component.ts